import { z } from 'zod';
import { idSchema, isoDateTimeSchema } from '../../common.js';
import { importedRecipeSchema } from '../recipes/index.js';
import { readScheduleRequestSchema, shiftGridSchema } from '../workSchedule/schema.js';
/**
 * AI relay contracts (WS-18, `docs/AI-PIPELINE.md`).
 *
 * These schemas are the ONLY thing the app server and the `relay/` process agree on. The
 * relay is deliberately not an open proxy: it exposes a fixed menu of named tasks, each
 * with a locked prompt template and an output schema from this file. Model output that
 * doesn't validate is retried once with the validation errors fed back, then rejected —
 * never partially applied (AI-PIPELINE "Output" rules).
 *
 * Nothing here imports Yjs or the server: the same module is used by the relay, the
 * server's `ai` module and the client's `useAiQuick` hook.
 */
// ---------------------------------------------------------------------------------------
// The task menu
// ---------------------------------------------------------------------------------------
/** The fixed task menu. Adding a task means adding a prompt template AND a schema here. */
export const AI_TASKS = [
    'clean-recipe',
    'parse-event',
    'parse-list',
    'edit-request',
    'read-shift-grid',
];
export const aiTaskSchema = z.enum(AI_TASKS);
/**
 * The tasks the client may invoke synchronously through `POST /api/ai/quick`. `clean-recipe`
 * is deliberately absent: it runs long, over scraped pages, and belongs on the job queue.
 */
export const AI_QUICK_TASKS = ['parse-event', 'parse-list', 'edit-request'];
export const aiQuickTaskSchema = z.enum(AI_QUICK_TASKS);
// ---------------------------------------------------------------------------------------
// Canonical unit vocabulary
// ---------------------------------------------------------------------------------------
/**
 * The closed unit token list the model may emit (AI-PIPELINE `clean-recipe` rules). The
 * first block is exactly the shared scaling core's `normalizeUnit` canonical vocabulary —
 * these convert. The second block is the count-units the spec adds: they are legal tokens
 * so the structured line keeps its shape, but `normalizeUnit` returns `null` for them and
 * the scaling core leaves them alone. Anything else ⇒ `unit: null`, phrase stays in `text`.
 */
export const AI_UNIT_TOKENS = [
    // convertible (scaling.ts UNIT_TABLE)
    'tsp',
    'tbsp',
    'cup',
    'floz',
    'pint',
    'quart',
    'gallon',
    'oz',
    'lb',
    'g',
    'kg',
    'ml',
    'l',
    // count-units — legal, but not convertible
    'clove',
    'can',
    'stick',
    'pinch',
    'bunch',
    'slice',
    'piece',
];
export const aiUnitTokenSchema = z.enum(AI_UNIT_TOKENS);
export const aiConfidenceSchema = z.enum(['high', 'medium', 'low']);
/**
 * `low` confidence never auto-applies (AI-PIPELINE: "the worker stores the result but does
 * NOT auto-apply; it queues for human review"). One predicate so the worker, the backfill
 * script and any future UI can't drift apart on what "good enough" means.
 */
export function isAutoApplicable(confidence) {
    return confidence !== 'low';
}
// ---------------------------------------------------------------------------------------
// Task: clean-recipe
// ---------------------------------------------------------------------------------------
export const cleanRecipeInputSchema = z.object({
    title: z.string().max(300).nullable().default(null),
    /** Raw scraped page text OR the stored imported blob. */
    text: z.string().min(1).max(120_000),
    /** Existing ingredient texts, when re-cleaning an already-imported recipe. */
    knownIngredientLines: z.array(z.string().max(500)).max(200).optional(),
    hints: z
        .object({
        /** e.g. "From forkinthekitchen.com." — display text, never a link. */
        sourceNote: z.string().max(300).optional(),
    })
        .optional(),
});
export const cleanIngredientSchema = z.object({
    /**
     * ALWAYS the faithful original line. The app renders `text` whenever the structured
     * fields are null, so a bad distill can never lose information.
     */
    text: z.string().min(1).max(500),
    /** Decimal, never a fraction string. `null` when unparseable (the line still ships). */
    quantity: z.number().nonnegative().max(100_000).nullable(),
    /** Upper bound of a range ("2–3 cloves"); `null` for a single amount. */
    quantityMax: z.number().nonnegative().max(100_000).nullable(),
    unit: aiUnitTokenSchema.nullable(),
    /** Bare normalized ingredient ("all-purpose flour") — the density-table key. */
    name: z.string().max(300).nullable(),
    /** Split-out preparation ("minced", "melted, cooled"). */
    prep: z.string().max(200).nullable(),
    optional: z.boolean(),
});
export const cleanRecipeOutputSchema = z.object({
    title: z.string().min(1).max(300),
    servings: z.number().int().min(1).max(100).nullable(),
    ingredients: z.array(cleanIngredientSchema).max(200),
    /** Imperative sentences, one action-cluster each. Empty when the input has no directions. */
    steps: z.array(z.string().min(1).max(5000)).max(200),
    notes: z.string().max(10_000).nullable(),
    confidence: aiConfidenceSchema,
});
// ---------------------------------------------------------------------------------------
// Task: parse-event
// ---------------------------------------------------------------------------------------
export const parseEventInputSchema = z.object({
    text: z.string().min(1).max(20_000),
    /** ISO timestamp the model resolves "tomorrow"/"next Friday" against. */
    now: isoDateTimeSchema.optional(),
    /** IANA zone the wall-clock times in `text` are written in. */
    timezone: z.string().max(80).optional(),
    /** Day of the week of `now` in `timezone` — handed over so the model never computes it. */
    weekday: z.string().max(20).optional(),
});
export const parseEventOutputSchema = z.object({
    title: z.string().min(1).max(200),
    /** `YYYY-MM-DD`; `null` when the text names no resolvable date. */
    date: z
        .string()
        .regex(/^\d{4}-\d{2}-\d{2}$/)
        .nullable(),
    /** `HH:MM` 24h local wall clock; `null` for an all-day event. */
    startTime: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .nullable(),
    endTime: z
        .string()
        .regex(/^\d{2}:\d{2}$/)
        .nullable(),
    allDay: z.boolean(),
    location: z.string().max(300).nullable(),
    notes: z.string().max(2000).nullable(),
    confidence: aiConfidenceSchema,
});
// ---------------------------------------------------------------------------------------
// Task: parse-list
// ---------------------------------------------------------------------------------------
export const parseListInputSchema = z.object({
    text: z.string().min(1).max(20_000),
    /** Existing item texts, so the model can avoid proposing duplicates. */
    existingItems: z.array(z.string().max(300)).max(500).optional(),
});
export const parseListOutputSchema = z.object({
    items: z
        .array(z.object({
        text: z.string().min(1).max(300),
        quantity: z.number().nonnegative().max(100_000).nullable(),
        unit: aiUnitTokenSchema.nullable(),
        /** Aisle-ish grouping hint ("produce", "dairy"); free text, purely advisory. */
        category: z.string().max(60).nullable(),
    }))
        .max(200),
    confidence: aiConfidenceSchema,
});
// ---------------------------------------------------------------------------------------
// Task: edit-request
// ---------------------------------------------------------------------------------------
export const editRequestInputSchema = z.object({
    /** What the user wants changed, in their own words. */
    instruction: z.string().min(1).max(4000),
    /** The current text being edited. */
    text: z.string().max(20_000),
    /** Free-text framing ("this is a recipe step", "this is a grocery item"). */
    context: z.string().max(500).optional(),
});
export const editRequestOutputSchema = z.object({
    text: z.string().max(20_000),
    /** One short sentence the UI can show next to the proposal. */
    summary: z.string().max(300).nullable(),
    confidence: aiConfidenceSchema,
});
// ---------------------------------------------------------------------------------------
// Task: read-shift-grid (2026-09-08, the work-schedule photo import)
// ---------------------------------------------------------------------------------------
/**
 * The one task that takes an image. The relay materialises `image` as a file in a scratch
 * directory and lets the model `Read` it (docs/AI-PIPELINE.md § read-shift-grid); the
 * prompt template only ever sees `{ imageFile, hints }`. Output is the verbatim grid
 * (`shiftGridSchema`, `modules/workSchedule`) — house rules are applied in code, not here.
 */
export const readShiftGridInputSchema = readScheduleRequestSchema;
export const readShiftGridOutputSchema = shiftGridSchema;
// ---------------------------------------------------------------------------------------
// Task registry — the one mapping every layer reads
// ---------------------------------------------------------------------------------------
export const AI_TASK_INPUT_SCHEMAS = {
    'clean-recipe': cleanRecipeInputSchema,
    'parse-event': parseEventInputSchema,
    'parse-list': parseListInputSchema,
    'edit-request': editRequestInputSchema,
    'read-shift-grid': readShiftGridInputSchema,
};
export const AI_TASK_OUTPUT_SCHEMAS = {
    'clean-recipe': cleanRecipeOutputSchema,
    'parse-event': parseEventOutputSchema,
    'parse-list': parseListOutputSchema,
    'edit-request': editRequestOutputSchema,
    'read-shift-grid': readShiftGridOutputSchema,
};
// ---------------------------------------------------------------------------------------
// Relay wire protocol (app server ⇄ relay, 127.0.0.1 only, bearer token)
// ---------------------------------------------------------------------------------------
export const RELAY_TASK_PATH = '/task';
export const RELAY_HEALTH_PATH = '/health';
/** The relay's loopback port on the VPS host (pm2, outside Docker — it shells out to `claude`). */
export const RELAY_DEFAULT_PORT = 3020;
export const relayTaskRequestSchema = z.object({
    task: aiTaskSchema,
    /** Task-specific; validated against `AI_TASK_INPUT_SCHEMAS[task]` by the relay. */
    payload: z.unknown(),
});
/** Why a relay call didn't produce a validated result. */
export const relayErrorCodeSchema = z.enum([
    'unauthorized',
    'invalid_request',
    'unknown_task',
    'invalid_payload',
    /** The model answered twice and neither answer validated — never partially applied. */
    'invalid_output',
    /** `claude` failed, timed out, or wasn't reachable at all. */
    'llm_failed',
]);
export const relayTaskResponseSchema = z.discriminatedUnion('ok', [
    z.object({
        ok: z.literal(true),
        task: aiTaskSchema,
        result: z.unknown(),
        /** 1 or 2 — 2 means the first answer failed validation and the retry succeeded. */
        attempts: z.number().int().min(1).max(2),
        /** True when the answer came from `relay/fixtures/` rather than a real model call. */
        mock: z.boolean().default(false),
    }),
    z.object({
        ok: z.literal(false),
        error: relayErrorCodeSchema,
        detail: z.string().max(2000).optional(),
        issues: z.array(z.unknown()).max(100).optional(),
    }),
]);
export const relayHealthSchema = z.object({
    status: z.literal('ok'),
    mock: z.boolean(),
    tasks: z.array(aiTaskSchema),
});
// ---------------------------------------------------------------------------------------
// `ai_jobs` — the outage-resilient queue (AI-PIPELINE failure mode 2)
// ---------------------------------------------------------------------------------------
/** Job kinds are task names: one queued job = one relay task call. */
export const aiJobKindSchema = aiTaskSchema;
export const aiJobStatusSchema = z.enum([
    'queued',
    'running',
    'done',
    /** Two model answers, neither validated. Terminal — a human looks at it. */
    'invalid',
    /** Attempts exhausted against an unreachable/erroring relay. Terminal. */
    'failed',
]);
/**
 * Payload envelope. `input` is the task payload the relay will validate; the rest is
 * bookkeeping the app needs when the answer eventually lands (which recipe this was for,
 * where the text came from, who asked).
 */
export const aiJobPayloadSchema = z.object({
    input: z.unknown(),
    /** Recipe this job cleans, when re-cleaning an existing doc entry (backfill). */
    recipeId: idSchema.optional(),
    /** Deep link only — a domain-only string is display text, never a link (AI-PIPELINE). */
    sourceUrl: z.string().url().max(2000).optional(),
    /** Tier-1 structured parse result, so a client polling the job still sees it. */
    tierOne: importedRecipeSchema.partial().optional(),
    requestedBy: idSchema.optional(),
    /** `import` (a user is waiting) vs `backfill` (nobody is). */
    origin: z.enum(['import', 'backfill', 'manual']).default('import'),
});
export const aiJobSchema = z.object({
    id: idSchema,
    kind: aiJobKindSchema,
    status: aiJobStatusSchema,
    payload: aiJobPayloadSchema,
    /** Validated task output once `status === 'done'`, else `null`. */
    result: z.unknown().nullable(),
    /** Last failure reason, for the review view. */
    error: z.string().max(2000).nullable(),
    attempts: z.number().int().min(0),
    createdAt: isoDateTimeSchema,
    updatedAt: isoDateTimeSchema,
    /** Earliest time the worker may pick this job up again (backoff). */
    nextAttemptAt: isoDateTimeSchema,
});
/** Max relay attempts before a job is parked as `failed`. */
export const AI_JOB_MAX_ATTEMPTS = 6;
/**
 * Backoff between relay attempts: 30s, 1m, 2m, 4m, 8m, capped at 15m. An LLM outage must
 * cost nothing and show nothing — the queue just waits (AI-PIPELINE failure mode 2).
 */
export function aiJobBackoffMs(attempts) {
    const base = 30_000 * 2 ** Math.max(0, attempts - 1);
    return Math.min(base, 15 * 60_000);
}
// ---------------------------------------------------------------------------------------
// REST surface — `/api/ai`
// ---------------------------------------------------------------------------------------
export const aiImportRecipeRequestSchema = z.object({
    url: z.string().url().max(2000),
});
/**
 * Three outcomes, and only the first one ever needs the LLM to be up:
 *
 * - `complete` — tier 1 (JSON-LD/microdata) got everything. Returned immediately, no job,
 *   no model call, no cost. This is the common case and it is never blocked on the LLM.
 * - `queued`  — tier 1 failed or came back partial. Whatever it *did* find ships now, the
 *   scraped text is snapshotted server-side, and a `clean-recipe` job is enqueued. The
 *   client polls `GET /api/ai/jobs/:id`; if the relay is down the job simply waits.
 * - `failed`  — the page couldn't be fetched at all (SSRF block, 404, timeout).
 */
export const aiImportRecipeResponseSchema = z.discriminatedUnion('status', [
    z.object({
        status: z.literal('complete'),
        recipe: importedRecipeSchema,
        warnings: z.array(z.string()).max(20).default([]),
    }),
    z.object({
        status: z.literal('queued'),
        jobId: idSchema,
        /** Best-effort tier-1 output — the family has something usable NOW. */
        partial: importedRecipeSchema.partial(),
        warnings: z.array(z.string()).max(20).default([]),
    }),
    z.object({
        status: z.literal('failed'),
        error: z.string().max(200),
    }),
]);
/** Polling shape for `GET /api/ai/jobs/:id`. Deliberately narrower than the stored row. */
export const aiJobStatusResponseSchema = z.object({
    id: idSchema,
    kind: aiJobKindSchema,
    status: aiJobStatusSchema,
    result: z.unknown().nullable(),
    attempts: z.number().int().min(0),
    updatedAt: isoDateTimeSchema,
});
export const aiQuickRequestSchema = z.object({
    task: aiQuickTaskSchema,
    text: z.string().min(1).max(20_000),
    /** Optional extra input merged into the task payload (`now`, `timezone`, `context`, …). */
    context: z.record(z.unknown()).optional(),
});
/**
 * `POST /api/ai/quick` returns a *proposal*. The server never writes a collaborative doc
 * for an interactive task — the client confirms and writes via Yjs itself (ROADMAP §1.3).
 */
export const aiQuickResponseSchema = z.object({
    task: aiQuickTaskSchema,
    proposal: z.unknown(),
});
/**
 * The soft 503 body. There is no error state for an LLM outage anywhere in the UI except
 * this one, and only when a human directly pressed a button (AI-PIPELINE failure mode 2).
 */
export const AI_NAPPING_REASON = 'assistant_napping';
export const aiUnavailableSchema = z.object({
    reason: z.literal(AI_NAPPING_REASON),
});
//# sourceMappingURL=index.js.map